import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Brain, Loader2, RotateCcw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  listAuraAgents,
  resetAuraAgentModel,
  shortAuraModelLabel,
  updateAuraAgentModel,
} from '@/lib/auraApi';
import { usePermissions } from '@/contexts/PermissionsContext';

export default function AuraAgentStudio() {
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission('transformations', 'update');
  const [agents, setAgents] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listAuraAgents();
      const list = Array.isArray(data.agents) ? data.agents : [];
      setAgents(list);
      setModelOptions(Array.isArray(data.model_options) ? data.model_options : []);
      const next = {};
      list.forEach((a) => {
        next[a.agent_id] = a.effective_model_id || a.default_model_id || '';
      });
      setDrafts(next);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to load agents';
      setError(String(detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveModel = async (agentId) => {
    if (!canUpdate) return;
    const modelId = drafts[agentId];
    if (!modelId) return;
    setSaving((s) => ({ ...s, [agentId]: true }));
    setError('');
    try {
      await updateAuraAgentModel(agentId, modelId);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to save model';
      setError(String(detail));
    } finally {
      setSaving((s) => ({ ...s, [agentId]: false }));
    }
  };

  const resetModel = async (agentId) => {
    if (!canUpdate) return;
    setSaving((s) => ({ ...s, [agentId]: true }));
    setError('');
    try {
      await resetAuraAgentModel(agentId);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to reset model';
      setError(String(detail));
    } finally {
      setSaving((s) => ({ ...s, [agentId]: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="aura-agent-studio">
      <div>
        <Link
          to="/transformations"
          className="mb-2 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Dashboard
        </Link>
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Transformations
        </p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight">Agent models</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Choose the Bedrock model for each transformation agent. MCP servers are not configured
          here — this area uses Halo&apos;s transformation orchestration path.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading agents…
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {agents.map((agent) => {
            const options =
              Array.isArray(agent.model_options) && agent.model_options.length
                ? agent.model_options
                : modelOptions;
            const isOpusDefault = agent.agent_id === 'aura-analysis';
            return (
              <Card key={agent.agent_id} data-testid={`aura-agent-card-${agent.agent_id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-cp-grad text-white">
                        <Brain className="h-4 w-4" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{agent.label}</CardTitle>
                        <CardDescription className="text-xs font-mono">
                          {agent.agent_id}
                        </CardDescription>
                      </div>
                    </div>
                    {isOpusDefault && (
                      <Badge variant="outline" className="border-amber-500/40 text-amber-200">
                        Default Opus
                      </Badge>
                    )}
                  </div>
                  <p className="pt-2 text-sm text-muted-foreground">{agent.description}</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-xs text-muted-foreground">
                    Effective:{' '}
                    <span className="text-foreground">
                      {shortAuraModelLabel(agent.effective_model_id)}
                    </span>
                    {agent.configured_model_id ? ' (override)' : ' (default)'}
                  </div>
                  <Select
                    value={drafts[agent.agent_id] || ''}
                    onValueChange={(v) =>
                      setDrafts((d) => ({ ...d, [agent.agent_id]: v }))
                    }
                    disabled={!canUpdate}
                  >
                    <SelectTrigger data-testid={`aura-model-select-${agent.agent_id}`}>
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent>
                      {options.map((opt) => (
                        <SelectItem key={opt.model_id} value={opt.model_id}>
                          {opt.label || shortAuraModelLabel(opt.model_id)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {canUpdate ? (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={!!saving[agent.agent_id]}
                        onClick={() => saveModel(agent.agent_id)}
                      >
                        {saving[agent.agent_id] ? 'Saving…' : 'Save model'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!!saving[agent.agent_id] || !agent.configured_model_id}
                        onClick={() => resetModel(agent.agent_id)}
                      >
                        <RotateCcw className="mr-1 h-3.5 w-3.5" />
                        Reset
                      </Button>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
